import React, { useEffect, useMemo, useState } from 'react';
import {
  Award,
  Download,
  Upload,
  AlertCircle,
  CheckCircle2,
  ArrowRight,
  ArrowLeft,
  Repeat,
  Trophy,
  Calendar,
  Users,
  Gift,
  Hash,
  MapPin,
  X,
} from 'lucide-react';
import { sweepstakesApi } from '../services/api';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const previousMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const formatMonthLabel = (yyyymm) => {
  const [y, m] = yyyymm.split('-').map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
};

function RuleRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
        <div className="text-sm text-gray-900">{value}</div>
      </div>
    </div>
  );
}

function ClientRulesCard({ client, onSelect, compact = false }) {
  return (
    <div
      className={`card ${onSelect ? 'cursor-pointer hover:shadow-md transition-shadow' : ''} ${
        compact ? 'p-4' : ''
      }`}
      onClick={onSelect}
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-lg font-semibold text-gray-900">{client.label}</h4>
        {onSelect && <ArrowRight className="w-5 h-5 text-primary-600" />}
      </div>
      <div className={compact ? 'grid grid-cols-2 gap-x-6 gap-y-0' : 'grid grid-cols-1 gap-y-0'}>
        <RuleRow icon={Calendar} label="Promotion Period" value={client.promotionPeriodLabel} />
        <RuleRow
          icon={Repeat}
          label="Number of Drawings"
          value={`${client.numberOfDrawings} monthly drawings`}
        />
        <RuleRow
          icon={Hash}
          label="Max Entries"
          value={`${client.maxEntriesPerDrawing} ${client.maxEntriesScope}`}
        />
        <RuleRow icon={Users} label="Winners per Drawing" value={client.winnersPerDrawing} />
        <RuleRow icon={Gift} label="Prize per Winner" value={client.prizeDescription} />
        <RuleRow
          icon={Repeat}
          label="Rollover of Non-winning Entries"
          value={
            client.rolloverEnabled
              ? 'Yes (non-winning entries carry forward to subsequent periods)'
              : 'No (entries do not carry forward)'
          }
        />
        <RuleRow icon={Trophy} label="Limit on Prizes" value={client.prizeLimit} />
      </div>
    </div>
  );
}

function SelectClientStep({ clients, onSelect }) {
  return (
    <div className="space-y-6">
      <div className="card">
        <div className="flex items-center gap-3 mb-3">
          <Award className="w-6 h-6 text-primary-600" />
          <h3 className="text-xl font-semibold text-gray-900">Sweepstakes Drawing</h3>
        </div>
        <p className="text-sm text-gray-600 mb-3">
          Run a monthly sweepstakes drawing for one client in two steps:
        </p>
        <ol className="list-decimal list-inside text-sm text-gray-700 space-y-1 ml-1">
          <li>
            <span className="font-medium">Pull entries</span> — download a CSV of anonymous codes
            (one row per entry) for the drawing month. Use it with any external random selection
            tool to pick the winners.
          </li>
          <li>
            <span className="font-medium">Upload winners</span> — paste the winning anonymous codes
            back here. The page shows each winner&apos;s contact details, and a background job
            records them against the sweepstakes results table.
          </li>
        </ol>
        <p className="text-sm text-gray-600 mt-3">
          Start by picking the client you&apos;re running the drawing for. Each client has different
          rules (entry cap, rollover, number of winners, prize) — those are shown on the card.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {clients.map((c) => (
          <ClientRulesCard key={c.key} client={c} onSelect={() => onSelect(c.key)} />
        ))}
      </div>
    </div>
  );
}

const WINNER_CSV_HEADER = [
  'anonymousCode',
  'entries',
  'firstName',
  'lastName',
  'phoneNumber',
  'secondaryPhoneNumber',
  'email',
  'languageCode',
  'cardOrdered',
];

function csvEscape(value) {
  if (value === null || value === undefined) return '';
  const raw = String(value);
  // Prefix formula-triggering characters so spreadsheet software (Excel,
  // Sheets) treats the cell as plain text instead of evaluating it. Checks
  // past leading whitespace/control characters (tab, CR, etc.) too — some
  // parsers still treat "\t=SUM(...)" as a formula, not just "=SUM(...)".
  const s = /^\s*[=+\-@]/.test(raw) ? `'${raw}` : raw;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadWinnerDetailsCsv({ client, month, winners }) {
  const lines = [WINNER_CSV_HEADER.join(',')];
  for (const w of winners) {
    lines.push(WINNER_CSV_HEADER.map((k) => csvEscape(w[k])).join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `sweepstakes-winner-details_${client}_${month}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function formatAddressLines(address) {
  if (!address) return [];
  const lines = [];
  if (address.line1) lines.push(address.line1);
  if (address.line2) lines.push(address.line2);
  const cityStateZip = [
    address.city,
    [address.state, address.postalCode].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');
  if (cityStateZip) lines.push(cityStateZip);
  if (address.country && address.country !== 'US') lines.push(address.country);
  return lines;
}

function ConfirmAddressModal({ winner, isPending, errorMessage, onConfirm, onCancel }) {
  if (!winner) return null;
  const addressLines = formatAddressLines(winner.address);
  const hasAddress = addressLines.length > 0;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">Confirm mailing address</h3>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close"
            disabled={isPending}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <p className="text-sm text-gray-700">
            The card order request was sent to FIS. Verify {winner.firstName} {winner.lastName}
            &apos;s mailing address below. If it looks correct, confirm to move them on to the
            FIS batch queue (<code className="text-xs">ELIGIBLE_FOR_FIS_BATCH_PROCESS</code>) so a
            card actually gets issued. If the address is wrong, cancel — fix it in the admin
            portal, then come back and re-open this dialog from the row&apos;s Action button.
          </p>
          <div className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2 text-sm">
            {hasAddress ? (
              <div className="space-y-0.5 text-gray-900">
                {addressLines.map((line, i) => (
                  <div key={i}>{line}</div>
                ))}
              </div>
            ) : (
              <div className="text-red-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                No mailing address on file for this winner.
              </div>
            )}
          </div>
          {errorMessage && (
            <div className="text-sm text-red-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> {errorMessage}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary"
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!hasAddress || isPending}
            className="btn-primary"
          >
            {isPending ? 'Sending to FIS…' : 'Confirm address & order card'}
          </button>
        </div>
      </div>
    </div>
  );
}

function WinnerActionCell({ winner, client, isCommitted, status, onValueLoad, onCardStatus, onConfirmAddress }) {
  if (!client.hasCardBasedFulfillment) {
    return (
      <span className="text-xs text-gray-400" title="Prize is fulfilled outside this tool (e.g. gift card)">
        —
      </span>
    );
  }
  if (!isCommitted) {
    return <span className="text-xs text-gray-400">Upload winners first</span>;
  }
  // Persistent "already loaded" state from sweepstakes_result.is_rewarded.
  // Takes precedence over any in-memory action status — if the DB says paid,
  // no further action is allowed regardless of session state.
  if (winner.valueLoaded) {
    return (
      <span
        className="inline-flex items-center gap-1 text-green-700 text-xs"
        title="sweepstakes_result.is_rewarded = TRUE for this drawing"
      >
        <CheckCircle2 className="w-4 h-4" /> Value already loaded
      </span>
    );
  }
  if (status?.state === 'pending') {
    return <span className="text-xs text-gray-500">Waiting for FIS…</span>;
  }
  if (status?.state === 'awaiting-address') {
    return (
      <div>
        <button
          type="button"
          className="btn-secondary text-xs px-2 py-1"
          onClick={() => onConfirmAddress(winner)}
        >
          Confirm address
        </button>
        <div className="mt-1 text-xs text-amber-700">
          Card order requested — address confirmation needed.
        </div>
      </div>
    );
  }
  if (status?.state === 'success') {
    return (
      <span className="inline-flex items-center gap-1 text-green-700 text-xs">
        <CheckCircle2 className="w-4 h-4" /> {status.message || 'Done'}
      </span>
    );
  }
  const errorBanner = status?.state === 'error' && (
    <div className="mt-1 text-xs text-red-700 flex items-center gap-1">
      <AlertCircle className="w-3 h-3" /> {status.message}
    </div>
  );
  if (winner.cardOrdered) {
    return (
      <div>
        <button
          type="button"
          className="btn-secondary text-xs px-2 py-1"
          onClick={() => onValueLoad(winner)}
        >
          Value Load ${client.prizeAmountUsd}
        </button>
        {errorBanner}
      </div>
    );
  }
  return (
    <div>
      <button
        type="button"
        className="btn-secondary text-xs px-2 py-1"
        onClick={() => onCardStatus(winner)}
      >
        Send card order request to FIS
      </button>
      {errorBanner}
    </div>
  );
}

function WinnerTable({
  winners,
  client,
  isCommitted = false,
  actionStatuses = {},
  onValueLoad = () => {},
  onCardStatus = () => {},
  onConfirmAddress = () => {},
}) {
  return (
    <div className="mt-4 overflow-x-auto border border-gray-200 rounded-lg">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
          <tr>
            <th className="px-3 py-2 text-left">Anonymous Code</th>
            <th className="px-3 py-2 text-right">Entries</th>
            <th className="px-3 py-2 text-left">First Name</th>
            <th className="px-3 py-2 text-left">Last Name</th>
            <th className="px-3 py-2 text-left">Phone</th>
            <th className="px-3 py-2 text-left">Secondary Phone</th>
            <th className="px-3 py-2 text-left">Email</th>
            <th className="px-3 py-2 text-left">Lang</th>
            <th className="px-3 py-2 text-left">Card Ordered</th>
            <th className="px-3 py-2 text-left">Action</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {winners.map((w) => (
            <tr key={w.anonymousCode}>
              <td className="px-3 py-2 font-mono text-xs">{w.anonymousCode}</td>
              <td className="px-3 py-2 text-right">{w.entries}</td>
              <td className="px-3 py-2">{w.firstName || '—'}</td>
              <td className="px-3 py-2">{w.lastName || '—'}</td>
              <td className="px-3 py-2">{w.phoneNumber || '—'}</td>
              <td className="px-3 py-2">{w.secondaryPhoneNumber || '—'}</td>
              <td className="px-3 py-2">{w.email || '—'}</td>
              <td className="px-3 py-2">{w.languageCode || '—'}</td>
              <td className="px-3 py-2">
                {w.cardOrdered ? (
                  <span className="inline-flex items-center gap-1 text-green-700">
                    <CheckCircle2 className="w-4 h-4" /> Yes
                  </span>
                ) : (
                  <span className="text-gray-500">No</span>
                )}
              </td>
              <td className="px-3 py-2">
                <WinnerActionCell
                  winner={w}
                  client={client}
                  isCommitted={isCommitted}
                  status={actionStatuses[w.anonymousCode]}
                  onValueLoad={onValueLoad}
                  onCardStatus={onCardStatus}
                  onConfirmAddress={onConfirmAddress}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DrawingStep({ client, onBack }) {
  const month = previousMonth();
  const monthLabel = formatMonthLabel(month);
  const [reportStatus, setReportStatus] = useState({ state: 'idle', message: '' });
  const [reportPreview, setReportPreview] = useState(null);
  const [winnersFile, setWinnersFile] = useState(null);
  const [winnersStatus, setWinnersStatus] = useState({ state: 'idle', message: '', details: [] });
  const [previewStatus, setPreviewStatus] = useState({ state: 'idle', message: '', details: [] });
  const [previewWinners, setPreviewWinners] = useState([]);
  const [actionStatuses, setActionStatuses] = useState({});
  const [confirmAddress, setConfirmAddress] = useState({
    winner: null,
    isPending: false,
    errorMessage: null,
  });

  const setActionStatus = (code, status) => {
    setActionStatuses((prev) => ({ ...prev, [code]: status }));
  };

  const handleValueLoad = async (winner) => {
    const current = actionStatuses[winner.anonymousCode];
    // Guard against double-fire — value load is one-shot per winner per row.
    // Also block if the winner has already been rewarded in a previous
    // session (sweepstakes_result.is_rewarded = TRUE).
    if (winner.valueLoaded) return;
    if (current?.state === 'pending' || current?.state === 'success') return;
    setActionStatus(winner.anonymousCode, { state: 'pending' });
    try {
      const res = await sweepstakesApi.processValueLoad({
        client: client.key,
        month,
        anonymousCode: winner.anonymousCode,
        consumerCode: winner.consumerCode,
      });
      const mockSuffix = res.data.mocked ? ' (mock — no real call)' : '';
      setActionStatus(winner.anonymousCode, {
        state: 'success',
        message: `Value load of $${res.data.amount} confirmed by FIS${mockSuffix}`,
      });
    } catch (err) {
      setActionStatus(winner.anonymousCode, { state: 'error', message: err.message });
    }
  };

  const handleCardStatusUpdate = async (winner) => {
    const current = actionStatuses[winner.anonymousCode];
    // Don't re-fire if an order has already been requested for this row.
    if (
      current?.state === 'pending' ||
      current?.state === 'awaiting-address' ||
      current?.state === 'success'
    ) {
      return;
    }
    setActionStatus(winner.anonymousCode, { state: 'pending' });
    try {
      await sweepstakesApi.processCardStatus({
        client: client.key,
        anonymousCode: winner.anonymousCode,
        consumerCode: winner.consumerCode,
        targetStatus: 'ELIGIBLE_TO_ORDER',
      });
      setActionStatus(winner.anonymousCode, { state: 'awaiting-address' });
      setConfirmAddress({ winner, isPending: false, errorMessage: null });
    } catch (err) {
      setActionStatus(winner.anonymousCode, { state: 'error', message: err.message });
    }
  };

  const handleReopenConfirmAddress = (winner) => {
    setConfirmAddress({ winner, isPending: false, errorMessage: null });
  };

  const handleConfirmAddressCancel = () => {
    setConfirmAddress({ winner: null, isPending: false, errorMessage: null });
  };

  const handleConfirmAddressSubmit = async () => {
    const winner = confirmAddress.winner;
    if (!winner) return;
    setConfirmAddress((prev) => ({ ...prev, isPending: true, errorMessage: null }));
    try {
      const res = await sweepstakesApi.processCardStatus({
        client: client.key,
        anonymousCode: winner.anonymousCode,
        consumerCode: winner.consumerCode,
        targetStatus: 'ELIGIBLE_FOR_FIS_BATCH_PROCESS',
      });
      const mockSuffix = res.data.mocked ? ' (mock — no real call)' : '';
      setActionStatus(winner.anonymousCode, {
        state: 'success',
        message: `Address confirmed; card queued for FIS batch${mockSuffix}`,
      });
      setConfirmAddress({ winner: null, isPending: false, errorMessage: null });
    } catch (err) {
      setConfirmAddress((prev) => ({ ...prev, isPending: false, errorMessage: err.message }));
    }
  };

  const handleGeneratePreview = async () => {
    setReportStatus({ state: 'preview-pending', message: 'Pulling entries…' });
    setReportPreview(null);
    try {
      const res = await sweepstakesApi.previewReport({ client: client.key, month });
      setReportPreview({
        entries: res.data.entries || [],
        summary: res.data.summary || { uniqueConsumers: 0, totalEntries: 0 },
      });
      setReportStatus({ state: 'preview-ready', message: '' });
    } catch (err) {
      setReportStatus({ state: 'error', message: err.message });
    }
  };

  const handleDownloadReport = async () => {
    setReportStatus({ state: 'pending', message: 'Saving to S3 and triggering Glue job…' });
    try {
      const result = await sweepstakesApi.downloadReport({ client: client.key, month });
      const url = URL.createObjectURL(result.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sweepstakes-entries_${client.key}_${month}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setReportStatus({
        state: 'success',
        message: 'Report downloaded.',
        s3Uri: result.s3Uri,
        s3Mocked: result.s3Mocked,
        glueJobRunId: result.glueJobRunId,
        glueError: result.glueError,
      });
    } catch (err) {
      setReportStatus({ state: 'error', message: err.message });
    }
  };

  const runPreview = async (file) => {
    setPreviewStatus({ state: 'pending', message: 'Looking up winner details…', details: [] });
    setPreviewWinners([]);
    try {
      const res = await sweepstakesApi.previewWinners({ client: client.key, month, file });
      setPreviewWinners(res.data.winners || []);
      setPreviewStatus({
        state: 'success',
        message: `${res.data.winnerCount} winners ready to record.`,
        details: [],
      });
    } catch (err) {
      setPreviewStatus({
        state: 'error',
        message: err.message,
        details: err.details || [],
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setWinnersFile(file);
    setWinnersStatus({ state: 'idle', message: '', details: [] });
    setActionStatuses({});
    setConfirmAddress({ winner: null, isPending: false, errorMessage: null });
    if (file) {
      runPreview(file);
    } else {
      setPreviewStatus({ state: 'idle', message: '', details: [] });
      setPreviewWinners([]);
    }
  };

  const handleUploadWinners = async (e) => {
    e.preventDefault();
    if (!winnersFile) {
      setWinnersStatus({ state: 'error', message: 'Choose a winners CSV first.', details: [] });
      return;
    }
    setWinnersStatus({
      state: 'pending',
      message: 'Validating and triggering Glue record-winners job…',
      details: [],
    });
    try {
      const res = await sweepstakesApi.uploadWinners({ client: client.key, month, file: winnersFile });
      setWinnersStatus({
        state: 'success',
        message: `${res.data.winnerCount} winners recorded (Glue run ${res.data.glueJobRunId}).`,
        details: [],
      });
    } catch (err) {
      setWinnersStatus({
        state: 'error',
        message: err.message,
        details: err.details || [],
      });
    }
  };

  return (
    <div className="space-y-6">
      <div className="card">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" /> Back to client selection
        </button>
        <div className="flex items-center gap-3 mt-3 mb-2">
          <Award className="w-6 h-6 text-primary-600" />
          <h3 className="text-xl font-semibold text-gray-900">
            {client.label} — Monthly Drawing
          </h3>
        </div>
        <p className="text-sm text-gray-600">
          Use the two steps below. <span className="font-medium">Step 1</span> downloads the
          file you&apos;ll feed into your random selection tool — and updates the entry wallets
          per {client.label}&apos;s rollover rule. <span className="font-medium">Step 2</span>{' '}
          records the picked winners back to the platform after you&apos;ve drawn them.
        </p>
      </div>

      <ClientRulesCard client={client} compact />

      <div className="card">
        <h4 className="text-lg font-semibold text-gray-900 mb-1">
          Step 1 — Pull the Entries File
        </h4>
        <p className="text-sm text-gray-600 mb-4">
          First click <span className="font-medium">Generate Preview</span> to load entries for{' '}
          <span className="font-medium">{monthLabel}</span> — the most recent completed month. The
          drawing month is locked because entries for the in-progress month are still being
          collected. The preview below is the exact file you&apos;ll get on{' '}
          <span className="font-medium">Approve</span> — one row per entry, so a consumer with 5
          entries shows up 5 times. A copy of the same file is saved to S3 for future reference,
          and the wallet-reset Glue job is triggered for you.
        </p>

        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col text-sm">
            <span className="text-gray-700 mb-1">Drawing Month</span>
            <div className="inline-flex items-center px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-900 font-medium w-40">
              {monthLabel}
            </div>
            <span className="text-xs text-gray-500 mt-1">
              Locked to the most recent completed month.
            </span>
          </div>
          <button
            type="button"
            onClick={handleGeneratePreview}
            disabled={reportStatus.state === 'preview-pending' || reportStatus.state === 'pending'}
            className="btn-secondary"
          >
            {reportStatus.state === 'preview-pending' ? 'Loading…' : 'Generate Preview'}
          </button>
          <button
            type="button"
            onClick={handleDownloadReport}
            disabled={
              reportStatus.state !== 'preview-ready' ||
              !reportPreview ||
              reportPreview.entries.length === 0
            }
            className="btn-primary"
            title={
              reportStatus.state !== 'preview-ready'
                ? 'Generate the preview first'
                : reportPreview && reportPreview.entries.length === 0
                ? 'No entries to confirm'
                : ''
            }
          >
            <Download className="w-4 h-4 mr-2" />
            {reportStatus.state === 'pending' ? 'Working…' : 'Approve'}
          </button>
        </div>

        <p className="mt-3 text-xs text-gray-500">
          Entry cap for {client.label}: {client.maxEntriesPerDrawing}{' '}
          {client.maxEntriesScope}.{' '}
          {client.resetWalletOnDrawing
            ? `On confirm, the entry wallets for ${client.label} are also updated — any entries above the cap are preserved as rollover; everything else is cleared so next month starts fresh.`
            : `${client.label}'s entries roll over the whole year, so confirming does not change wallet balances.`}
        </p>

        {reportStatus.state === 'preview-ready' && reportPreview && (
          <div className="mt-4">
            <div className="text-sm text-gray-700 mb-2">
              <span className="font-medium">
                {reportPreview.summary.uniqueConsumers}
              </span>{' '}
              eligible consumer{reportPreview.summary.uniqueConsumers === 1 ? '' : 's'} ·{' '}
              <span className="font-medium">{reportPreview.summary.totalEntries}</span> total
              entries (post-cap). The table below is exactly what the downloaded CSV will
              contain — <span className="font-medium">one row per entry</span>, so a consumer
              with 5 entries appears 5 times. Click{' '}
              <span className="font-medium">Approve</span> to download this file; a copy is also
              saved to S3 for future reference and wallets are updated per the client&apos;s
              rollover rule.
            </div>
            {reportPreview.entries.length === 0 ? (
              <div className="text-sm text-amber-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" /> No eligible entries for this month — nothing
                to confirm.
              </div>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-96 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 uppercase text-xs sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-right w-16">#</th>
                      <th className="px-3 py-2 text-left">anonymous_code</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {reportPreview.entries.map((e, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2 text-right text-gray-400 text-xs">{i + 1}</td>
                        <td className="px-3 py-2 font-mono text-xs">{e.anonymousCode}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {reportStatus.state === 'success' && (
          <div className="mt-4 text-sm text-green-700">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> {reportStatus.message}
            </div>
            {reportStatus.s3Uri && (
              <div className="text-gray-600 mt-1 ml-6 break-all">
                Audit copy saved to{' '}
                <code className="text-xs">{reportStatus.s3Uri}</code>
                {reportStatus.s3Mocked ? ' (mock — no real upload)' : ''}.
              </div>
            )}
            {reportStatus.glueError && (
              <div className="text-amber-700 mt-1 ml-6">
                Note: Glue wallet-reset job failed to start: {reportStatus.glueError}
              </div>
            )}
            <div className="text-gray-600 mt-1 ml-6">
              Next: use the downloaded CSV with your random selection tool to pick{' '}
              {client.winnersPerDrawing} winner{client.winnersPerDrawing === 1 ? '' : 's'}, then
              come back to Step 2 below.
            </div>
          </div>
        )}
        {reportStatus.state === 'error' && (
          <div className="mt-4 text-sm text-red-700 flex items-center gap-2">
            <AlertCircle className="w-4 h-4" /> {reportStatus.message}
          </div>
        )}
      </div>

      <div className="card">
        <h4 className="text-lg font-semibold text-gray-900 mb-1">
          Step 2 — Upload the Winners
        </h4>
        <p className="text-sm text-gray-600 mb-2">
          Once your random selection tool has picked the {client.winnersPerDrawing} winning{' '}
          <code>anonymous_code</code>{client.winnersPerDrawing === 1 ? '' : 's'} from the Step 1
          file, save them to a CSV (one code per line — a header row like{' '}
          <code>anonymous_code</code> is fine, but optional) and pick the file below.
        </p>
        <p className="text-sm text-gray-600 mb-4">
          As soon as you choose a file, the page validates it and shows each winner&apos;s contact
          details so you can sanity-check the draw. Once that looks right, click{' '}
          <span className="font-medium">Upload Winners</span> to record them; you can also
          download the details as a CSV for your own records.
        </p>
        <form onSubmit={handleUploadWinners} className="flex flex-wrap items-end gap-4">
          <label className="flex flex-col text-sm">
            <span className="text-gray-700 mb-1">Winners CSV</span>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
              className="input w-72"
            />
          </label>
          <button
            type="submit"
            disabled={
              !winnersFile ||
              previewStatus.state !== 'success' ||
              winnersStatus.state === 'pending'
            }
            className="btn-primary"
          >
            <Upload className="w-4 h-4 mr-2" />
            {winnersStatus.state === 'pending' ? 'Working…' : 'Upload Winners'}
          </button>
          {previewStatus.state === 'success' && previewWinners.length > 0 && (
            <button
              type="button"
              onClick={() =>
                downloadWinnerDetailsCsv({
                  client: client.key,
                  month,
                  winners: previewWinners,
                })
              }
              className="btn-secondary"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Winner Details
            </button>
          )}
        </form>

        {previewStatus.state === 'pending' && (
          <div className="mt-4 text-sm text-gray-600">{previewStatus.message}</div>
        )}
        {previewStatus.state === 'error' && (
          <div className="mt-4 text-sm text-red-700">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> {previewStatus.message}
            </div>
            {previewStatus.details.length > 0 && (
              <ul className="mt-2 list-disc list-inside space-y-1">
                {previewStatus.details.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            )}
            <div className="mt-2 text-gray-600">
              Fix the issues above and pick the file again — nothing has been recorded yet.
            </div>
          </div>
        )}
        {previewStatus.state === 'success' && previewWinners.length > 0 && (
          <>
            <div className="mt-4 text-sm text-gray-700">
              Showing {previewWinners.length} winner
              {previewWinners.length === 1 ? '' : 's'} from your CSV. Double-check the names and
              contact info below — if anything looks wrong, replace the file. When you&apos;re
              ready, click <span className="font-medium">Upload Winners</span> to record them.
            </div>
            <WinnerTable
              winners={previewWinners}
              client={client}
              isCommitted={winnersStatus.state === 'success'}
              actionStatuses={actionStatuses}
              onValueLoad={handleValueLoad}
              onCardStatus={handleCardStatusUpdate}
              onConfirmAddress={handleReopenConfirmAddress}
            />
          </>
        )}

        {winnersStatus.state === 'success' && (
          <div className="mt-4 text-sm text-green-700">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> {winnersStatus.message}
            </div>
            <div className="text-gray-600 mt-1 ml-6">
              The winners are now saved against this drawing.
              {client.hasCardBasedFulfillment ? (
                <>
                  {' '}Use the per-row <span className="font-medium">Action</span> buttons in the
                  table above to disburse prizes. There are two paths depending on whether the
                  winner already has a card:

                  <ul className="list-disc list-inside mt-2 space-y-2">
                    <li>
                      <span className="font-medium">Card Ordered = Yes</span> — click{' '}
                      <span className="font-medium">Value Load ${client.prizeAmountUsd}</span> to
                      put the prize on their card.{' '}
                      <span className="font-medium text-amber-700">
                        This button is one-shot per winner — only click it once.
                      </span>{' '}
                      Once it succeeds, the button is replaced by a confirmation and can&apos;t
                      be re-clicked. If something goes wrong mid-flight, the error message is
                      shown inline so you can decide whether to retry manually.
                    </li>
                    <li>
                      <span className="font-medium">Card Ordered = No</span> — this is a two-day
                      flow:
                      <ol className="list-decimal list-inside mt-1 ml-4 space-y-1">
                        <li>
                          Click <span className="font-medium">Send card order request to FIS</span>.
                        </li>
                        <li>
                          A popup shows the winner&apos;s mailing address. Verify it, then click{' '}
                          <span className="font-medium">Confirm address &amp; order card</span> —
                          this requests a card on their behalf and queues them for the next FIS
                          card-creation batch.
                        </li>
                        <li>
                          <span className="font-medium">Come back tomorrow.</span> FIS takes about
                          a day to create and sync the new card. Once that&apos;s done, this
                          winner&apos;s Card Ordered will flip to Yes and the row&apos;s Action
                          column will show the{' '}
                          <span className="font-medium">Value Load ${client.prizeAmountUsd}</span>{' '}
                          button. Click it then to load the prize onto the freshly issued card —
                          again, only once.
                        </li>
                      </ol>
                    </li>
                  </ul>
                </>
              ) : (
                <>
                  {' '}Prize fulfillment for {client.label} (
                  {client.prizeDescription.toLowerCase()}) happens outside this tool — no further
                  actions are needed here.
                </>
              )}
            </div>
          </div>
        )}
        {winnersStatus.state === 'error' && (
          <div className="mt-4 text-sm text-red-700">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" /> {winnersStatus.message}
            </div>
            {winnersStatus.details.length > 0 && (
              <ul className="mt-2 list-disc list-inside space-y-1">
                {winnersStatus.details.map((d, i) => (
                  <li key={i}>{d}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <ConfirmAddressModal
        winner={confirmAddress.winner}
        isPending={confirmAddress.isPending}
        errorMessage={confirmAddress.errorMessage}
        onConfirm={handleConfirmAddressSubmit}
        onCancel={handleConfirmAddressCancel}
      />
    </div>
  );
}

export default function SweepstakesDrawing() {
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsError, setClientsError] = useState(null);
  const [selectedClientKey, setSelectedClientKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    sweepstakesApi
      .listClients()
      .then((res) => {
        if (cancelled) return;
        setClients(res?.data || []);
        setClientsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setClientsError(err.message);
        setClientsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedClient = useMemo(
    () => clients.find((c) => c.key === selectedClientKey) || null,
    [clients, selectedClientKey]
  );

  if (clientsLoading) {
    return <div className="card">Loading clients…</div>;
  }
  if (clientsError) {
    return (
      <div className="card text-red-700 flex items-center gap-2">
        <AlertCircle className="w-5 h-5" /> Failed to load clients: {clientsError}
      </div>
    );
  }

  if (selectedClient) {
    return <DrawingStep client={selectedClient} onBack={() => setSelectedClientKey(null)} />;
  }

  return <SelectClientStep clients={clients} onSelect={setSelectedClientKey} />;
}
